import React, { useRef, useState, useEffect } from "react";
import {
    Box,
    Link,
    useTheme,
    useMediaQuery,
    alpha,
    darken,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    Paper,
} from '@material-ui/core';

import { useMeasure } from "react-use";
import { useImage } from '../../../utils/hooks';
import { useCalculator } from '../../../state/calculatorState';
import { resizeCanvas, ordinal, precision, formatMoney } from '../../../utils/utils';
import { isUndefined } from '../../../core/calculatorUtils';
import { DARK_MODE } from '../../../state/themeState';
import marker_white from '../../../assets/marker2_white.png';
import marker_grey from '../../../assets/marker2_grey.png';
import { data } from '../../../data/PercentileRange'
import { ATO_PERCENTILE_DISTRIBUTION, ATO_PERCENTILE_DISTRIBUTION_HISTORIC } from '../../../constants';

import { TaxChartSlider, StyledTableRow } from '../../../components/common/CustomComponents'

function createData(label, description) {
    return { label, description };
}



export const PercentileRangeComponent = () => {
    const [, darkIcon] = useImage(marker_white)
    const [, lightIcon] = useImage(marker_grey)
    const ref = useRef();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('xs'));

    //income
    let [{ income },] = useCalculator();



    let currentSalaryPercentile = 1;
    const currentYear = data[0] ?? undefined;
    const currentYearData = currentYear.data ?? undefined
    const currentYearMeta = currentYear.metadata ?? undefined

    if (currentYear && currentYearData && currentYearMeta) {

        for (let i = 0; i < currentYearData.length; i++) {
            const from = currentYearData[i].income.from;
            const to = currentYearData[i].income.to === 0 ? income.a + 1 : currentYearData[i].income.to
            if (income.a >= from && income.a <= to) {
                currentSalaryPercentile = currentYearData[i].percentile;
            }
        }
    }

    const [percentile, setPercentile] = useState(currentSalaryPercentile);

    const [salaryPercentile, setSalaryPercentile] = useState(currentSalaryPercentile);
    if (salaryPercentile !== currentSalaryPercentile) {
        setSalaryPercentile(currentSalaryPercentile);
        setPercentile(currentSalaryPercentile);
    }

    let percentileIndex = percentile - 1;

    let icon = (theme.palette.type === DARK_MODE) ? darkIcon : lightIcon;
    if (!icon) icon = null;

    const [refContainer, { width }] = useMeasure();

    const [redrawCount, setRedrawCount] = useState(0);
    useEffect(() => {
        const timer = setTimeout(() => { setRedrawCount(1) }, 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (width === 0) return
        let canvas = ref.current;
        let context = canvas.getContext('2d');
        const { ratio } = resizeCanvas(canvas, context, 120);
        draw(context, canvas, theme, ratio, percentile, icon, data);
    }, [percentile, width, redrawCount, icon, theme, currentYearMeta]);


    let height = 350;
    if (small) height = 200;

    const modifyPercentile = (_, value) => {
        setPercentile(value);
    }

    if (isUndefined(data)) return false

    let percentContribution = currentYearData[percentileIndex].all.tax / currentYearMeta.totalTax;
    percentContribution = precision(percentContribution * 100, 2);

    let maleContribution = currentYearData[percentileIndex].male.total / currentYearData[percentileIndex].all.total
    maleContribution = precision(maleContribution * 100, 2);

    let femaleContribution = currentYearData[percentileIndex].female.total / currentYearData[percentileIndex].all.total
    femaleContribution = precision(femaleContribution * 100, 2);

    const RESULTS_TABLE = [
        createData(
            'Percentile', `${percentile}${ordinal(percentile)} percentile`
        ),
        createData(
            'Income Range',
            <Box display="flex" flexDirection="column">
                <Typography component="span" variant="h4" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                    {`${currentYearData[percentileIndex].income.description}`}
                </Typography>
            </Box>,
        ),
        createData(
            'Gender Mix',
            <Box display="flex" flexDirection="row">
                <Box display="flex" flexDirection="column" style={{ paddingRight: 10 }}>
                    <Typography component="span" variant="h4" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                        {`${maleContribution}%`}

                    </Typography>
                    <Typography variant="body2" style={{ color: theme.palette.primary.main }}>
                        {`Male`}
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                    <Typography component="span" variant="h4" style={{ fontSize: 16, color: theme.palette.secondary.main }}>
                        {`${femaleContribution}%`}

                    </Typography>
                    <Typography variant="body2" style={{ color: theme.palette.secondary.main }}>
                        {`Female`}
                    </Typography>
                </Box>
            </Box>
        ),
        createData(
            'Tax Burden',
            <Box display="flex" flexDirection="column" style={{ paddingRight: 10 }}>
                <Typography component="span" variant="h4" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                    {`$${formatMoney(currentYearData[percentileIndex].all.averageTax, 0)} average tax paid`}

                </Typography>
                <Typography variant="body2" style={{ color: theme.palette.primary.main }}>
                    {`${percentContribution}% of total tax revenue`}
                </Typography>
            </Box>
        ),
    ];
    //
    //

    return (
        <Box style={{ padding: '0px 20px', backgroundColor: theme.palette.background.paper }} ref={refContainer}>
            <Box style={{ paddingTop: 10 }}>
                <Box display="flex" flexGrow={1} style={{ height: `${height}px`, width: '100%' }}>
                    <canvas ref={ref} width={'100%'} height={`${height}px`} />
                </Box>
            </Box>

            <Box style={{ padding: 12, paddingTop: 10 }}>
                <TaxChartSlider
                    valueLabelDisplay="off"
                    min={1}
                    max={Number(100)}
                    step={1}
                    aria-label="Adjust income"
                    value={percentile}
                    onChange={modifyPercentile}
                />
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="flex-start">
                <TableContainer component={Paper}>
                    <Table aria-label="Tax table">
                        <TableBody>
                            {RESULTS_TABLE.map((row, index) => (
                                <StyledTableRow key={row.label}>
                                    <TableCell size="small" width='30%' align="left" style={{ fontSize: 16 }}>
                                        <Typography variant="h2">{row.label}</Typography>
                                    </TableCell>
                                    <TableCell width='70%' align="left">
                                        <Typography component="span" variant="h4" style={{ fontSize: 16, color: (index === 0 ? theme.palette.secondary.main : theme.palette.primary.main) }}>
                                            {row.description}
                                        </Typography>
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>


            </Box>
            <Box style={{ paddingTop: 20 }}>
                {data.length === 1 && (
                    <Typography variant="body2">Source: ATO percentile distribution dataset <Link href={ATO_PERCENTILE_DISTRIBUTION} rel="noopener noreferrer" target="_blank" >{currentYear.year}</Link> </Typography>
                )}
                {data.length === 2 && (
                    <Typography variant="body2">Source: ATO percentile distribution dataset <Link href={ATO_PERCENTILE_DISTRIBUTION} rel="noopener noreferrer" target="_blank" >{currentYear.year}</Link> and  <Link href={ATO_PERCENTILE_DISTRIBUTION_HISTORIC} rel="noopener noreferrer" target="_blank" >{data[1].year}</Link>
                    </Typography>
                )}
                <Typography style={{ opacity: 0.5 }} variant="body2">Data for {currentYear.year} is currently the most recent dataset available</Typography>
            </Box>

        </Box>

    )
}





const draw = (ctx, canvas, theme, ratio, percentile, icon, data) => {


    if (isUndefined(data)) return false;

    let margin = 12.5 * ratio;
    let chartWidth = (canvas.width / 1) - (2 * margin);
    let chartHeight = canvas.height - (4 * margin);
    let chartY = margin * 3;
    let chartX = margin;
    let labelY = margin * 1.0;


    const bgColor = alpha(theme.palette.grey[900], 0.1)



    // bg
    ctx.beginPath();
    // ctx.rect(x1, y1, x2, y2);
    ctx.rect(chartX, chartY, chartWidth, chartHeight);
    ctx.fillStyle = bgColor;
    ctx.fill();

    let male_colA = [
        theme.palette.primary.main,
        darken(theme.palette.primary.dark, 0.1)
    ]



    let female_colA = [
        theme.palette.secondary.main,
        darken(theme.palette.secondary.dark, 0.1)

    ]


    let max = 0
    let y = 0;
    let i = 0;

    for (y = 0; y < data.length; y++) {
        max = Math.max(max, data[y].metadata.max)
    }
    const stepX = chartWidth / 100;
    let heightRatio = chartHeight / max;


    let percentilePos = chartX + stepX * (percentile - 1);

    let years = []
    if (data[0]) years.push(data[0].year)
    if (data[1]) years.push(data[1].year)

    for (y = 0; y < years.length; y++) {
        let currentYearData = data[y].data;
        let px = chartX

        if (isUndefined(currentYearData)) break

        max = 0;
        for (i = 0; i < currentYearData.length; i++) {
            max = Math.max(currentYearData[i].male.total, max)
            max = Math.max(currentYearData[i].female.total, max)
        }
        heightRatio = chartHeight / max

        for (i = 0; i < currentYearData.length; i++) {

            let h = currentYearData[i].male.total * heightRatio
            let w = stepX / 2 - 0 * ratio
            let py = chartY + chartHeight - h
            ctx.beginPath();
            ctx.rect(px, py, w, h);
            ctx.fillStyle = percentile > i ? `${alpha(male_colA[y], 0.85)}` : `${darken(male_colA[y], 0.5)}`
            ctx.fill();

            h = currentYearData[i].female.total * heightRatio
            py = chartY + chartHeight - h
            px += stepX / 2
            ctx.beginPath();
            ctx.rect(px, (py), w, h);
            ctx.fillStyle = percentile > i ? `${alpha(female_colA[y], 0.85)}` : `${darken(female_colA[y], 0.5)}`
            ctx.fill();

            px += stepX / 2
        }

        ctx.beginPath();
        ctx.rect(percentilePos + stepX / 2, chartY - chartHeight * 1, 1, chartHeight * 2);
        ctx.fillStyle = `${theme.palette.text.primary}`;
        ctx.fill();
    }


    if (icon) {
        let px = percentilePos - icon.width * 0.5
        px += stepX / 2;
        ctx.drawImage(icon, px, chartY + chartHeight * 1.0, 25, 60);
    }

    let labelXPos = percentile < 50 ? percentilePos + margin + (5 * ratio) : percentilePos + (0 * ratio)
    let labelAlign = percentile < 50 ? "left" : "right"

    let labelYpos = labelY;

    ctx.font = `${theme.typography.body1.fontSize * ratio * 0.85}px ${theme.typography.body1.fontFamily[0]}`;
    ctx.fillStyle = `${theme.palette.text.primary}`;
    ctx.textAlign = labelAlign
    ctx.fillText(`${percentile}${ordinal(percentile)} percentile`, labelXPos, labelYpos - (2.5 * ratio));


    // ctx.font = `${theme.typography.body1.fontSize * ratio * 0.75}px ${theme.typography.body1.fontFamily[0]}`;
    // ctx.fillStyle = `${barColor}`;
    // ctx.textAlign = labelAlign
    // ctx.fillText(`You're in the ${percentile}% income band`, labelXPos, labelYpos + (12 * ratio));


    if (years.length >= 2) {

        ctx.beginPath();
        ctx.fillStyle = ctx.strokeStyle = alpha(theme.palette.primary.main, 0.9);
        ctx.moveTo(30 * ratio, 40 * ratio)
        ctx.lineTo(40 * ratio, 40 * ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Male(${years[0]})`, 50 * ratio, 45 * ratio);

        ctx.beginPath();
        ctx.fillStyle = ctx.strokeStyle = darken(theme.palette.primary.dark, 0.2);
        ctx.moveTo(30 * ratio, 55 * ratio)
        ctx.lineTo(40 * ratio, 55 * ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Male(${years[1]})`, 50 * ratio, 60 * ratio);


        ctx.beginPath();
        ctx.fillStyle = ctx.strokeStyle = alpha(theme.palette.secondary.main, 0.9);
        ctx.moveTo(140 * ratio, 40 * ratio)
        ctx.lineTo(150 * ratio, 40 * ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.textAlign = "left";
        ctx.fillText(`Female(${years[0]})`, 160 * ratio, 45 * ratio);
        ctx.stroke()

        ctx.beginPath();
        ctx.fillStyle = ctx.strokeStyle = darken(theme.palette.secondary.dark, 0.2);
        ctx.moveTo(140 * ratio, 55 * ratio)
        ctx.lineTo(150 * ratio, 55 * ratio)
        ctx.lineWidth = 3 * ratio;
        ctx.stroke()
        ctx.textAlign = "left";
        ctx.fillText(`Female(${years[1]})`, 160 * ratio, 60 * ratio);
    }
}

export default PercentileRangeComponent